import './AudiencePickerModal.scss';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '../Button';
import { Appearance, Design, Intent, Size } from '../designConstants';
import {
  DEMO_GROUPINGS,
  DemoGroupType,
  OVERLAPPING_DEMO_LOOKUP
} from './audience-picker-constants';

interface DemoGroupSectionProps {
  group: DemoGroupType;
  selections: Record<string, boolean>;
  onSelect: (demoValue: string) => void;
}

const DemoGroupSection = ({ group, selections, onSelect }: DemoGroupSectionProps) => (
  <div className='demo-group'>
    <strong>{group}:</strong>
    <div className='demo-buttons'>
      {Object.entries(DEMO_GROUPINGS[group]).map(([prettyDemo, demoValue]) => (
        <Button
          key={prettyDemo}
          size={Size.SM}
          appearance={selections[demoValue] ? Appearance.FILLED : Appearance.OUTLINED}
          design={selections[demoValue] ? Design.BRAND : Design.NEUTRAL}
          onClick={() => onSelect(demoValue)}
        >
          {prettyDemo}
        </Button>
      ))}
    </div>
  </div>
);

interface AudiencePickerModalProps {
  selectedDemos: Record<string, boolean>;
  setSelectedDemos: (demos: Record<string, boolean>) => void;
  onClose: () => void;
}

export const AudiencePickerModal = ({
  selectedDemos,
  setSelectedDemos,
  onClose
}: AudiencePickerModalProps) => {
  // Local working copy -- only committed to the caller on Save, matching the original's
  // stage-then-commit flow (Cancel/closing discards in-progress picks).
  const [selections, setSelections] = useState(selectedDemos);

  const onSelect = (demoValue: string) => {
    // Picking a "Meta" demo (HH, A18+, etc.) replaces the whole selection -- it's a standalone
    // catch-all, not one that combines with others.
    if (Object.values(DEMO_GROUPINGS.Meta).includes(demoValue)) {
      setSelections({ [demoValue]: true });
      return;
    }
    setSelections(current => {
      const withoutMeta = Object.fromEntries(
        Object.entries(current).filter(([key]) => !Object.values(DEMO_GROUPINGS.Meta).includes(key))
      );
      const withoutOverlaps = Object.fromEntries(
        Object.entries(withoutMeta).filter(
          ([key]) => !(OVERLAPPING_DEMO_LOOKUP[demoValue] ?? []).includes(key)
        )
      );
      return { ...withoutOverlaps, [demoValue]: !current[demoValue] };
    });
  };

  return (
    <Modal show className='audience-picker-modal' onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Select Demographics</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(Object.keys(DEMO_GROUPINGS) as DemoGroupType[]).map(group => (
          <DemoGroupSection key={group} group={group} selections={selections} onSelect={onSelect} />
        ))}
      </Modal.Body>
      <Modal.Footer className='audience-picker-footer'>
        <Button
          design={Intent.ERROR}
          appearance={Appearance.OUTLINED}
          onClick={() => setSelections({})}
        >
          Clear
        </Button>
        <div className='audience-picker-footer-actions'>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
            Cancel
          </Button>
          <Button
            design={Intent.SUCCESS}
            onClick={() => {
              // Deselecting a demo sets its value to false rather than deleting the key (needed
              // so the button grid's `selections[demoValue]` check still reads a stable falsy
              // value), so filter to only the `true` entries here -- callers read this back via
              // Object.keys(demos), which doesn't care about a key's value.
              setSelectedDemos(
                Object.fromEntries(Object.entries(selections).filter(([, value]) => value))
              );
              onClose();
            }}
          >
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
